"use client";

import { useState, useEffect, memo } from "react";
import type { PropsWithChildren } from "react";

import { BrowserHooks } from "@/src/hook";
import { StrapiComponentType } from "@/src/type";
import { Log } from "@/src/util";

import { AppContext, DEFAULT_HEADER, STATIC_PAGE_HEADER } from "./AppContext";

interface AppProviderProps {
  defaultQuery?: string;
  defaultHeader?: StrapiComponentType.Partial.Header;
  defaultBackgroundColor?: string;
  defaultDomain?: string;
}

function AppProvider({ defaultQuery, defaultHeader, defaultBackgroundColor, defaultDomain, children }: PropsWithChildren<AppProviderProps>) {
  const [profile, setProfileToStorage] = BrowserHooks.useLocalStorage("PROFILE", {});
  const [header, setHeader] = useState<StrapiComponentType.Partial.Header>(defaultHeader ?? DEFAULT_HEADER);
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(defaultBackgroundColor ?? "transparent");
  const [pageSliderData, setPageSliderData] = useState<{ activeIndex?: number }>({});

  /**
   * on shalow routing make sure to reset the header/bg color
   * log out changes in state
   */
  /*
  useEffect(() => {
    setHeader(defaultHeader ?? DEFAULT_HEADER);
    setBackgroundColor(defaultBackgroundColor ?? "transparent");
  }, [defaultHeader, defaultBackgroundColor]);
  */

  /**
   * log out the intiation properties of the application provider
   */
  useEffect(() => {
    Log.logToConsoleDebug(
      "AppProvider.tsx",
      "App provider initiated context",
      [{ defaultQuery, defaultHeader, defaultBackgroundColor, defaultDomain }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [defaultQuery, defaultHeader, defaultBackgroundColor, defaultDomain]);

  /**
   * on shalow routing make sure to reset the header/bg color
   * log out changes in state
   */
  useEffect(() => {
    Log.logToConsoleDebug(
      "AppProvider.tsx",
      "App provider updated context",
      [{ header, backgroundColor }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [header, backgroundColor]);

  function setProfileImageUrl(profileImageUrl: string) {
    const _profile = JSON.parse(JSON.stringify(profileImageUrl));
    setProfileToStorage({ ..._profile, profileImageUrl });
  }

  function setProfile(firstName: string, profileImageUrl: string) {
    setProfileToStorage({ firstName, profileImageUrl });
  }

  function checkAndSetHeader({
    toChangeHeader,
    toChangeBackgroundColor,
    hasNudge,
    activeSlideIndex,
  }: {
    toChangeHeader?: StrapiComponentType.Partial.Header;
    toChangeBackgroundColor?: string;
    hasNudge?: boolean;
    activeSlideIndex?: number;
  }) {
    const evalHeader: StrapiComponentType.Partial.Header = toChangeHeader ?? DEFAULT_HEADER;

    if (
      evalHeader?.flowLogo !== header?.flowLogo ||
      (evalHeader?.bottomShadow ?? false) !== (header?.bottomShadow ?? false) ||
      (evalHeader?.darkMenuIcon ?? false) !== (header?.darkMenuIcon ?? false) ||
      (evalHeader?.blurBackground ?? false) !== (header?.blurBackground ?? false) ||
      (evalHeader?.navOff ?? false) !== (header?.navOff ?? false) ||
      (evalHeader?.logoOff ?? false) !== (header?.logoOff ?? false) ||
      (evalHeader?.inheritBackgroundColorOff ?? false) !== (header?.inheritBackgroundColorOff ?? false)
    ) {
      setHeader(evalHeader);
    }

    if (backgroundColor !== (toChangeBackgroundColor ?? "transparent")) {
      setBackgroundColor(toChangeBackgroundColor ?? "transparent");
    }

    if (hasNudge) {
      setPageSliderData({ activeIndex: activeSlideIndex });
    }
  }

  return (
    <AppContext.Provider
      value={{
        defaultQuery: defaultQuery ?? "",
        profile,
        header,
        backgroundColor: backgroundColor ?? "transparent",
        defaultDomain: defaultDomain ?? "",
        pageSliderData,
        setProfileImageUrl,
        setProfile,
        setHeader: checkAndSetHeader,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

const MemorizedProvider = memo(AppProvider);
export default MemorizedProvider;
export { DEFAULT_HEADER, STATIC_PAGE_HEADER };
